import { Pencil } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

interface EditableCellProps {
  value: string;
  rowIndex: number;
  colIndex: number;
  isEditing: boolean;
  onCellEdit: (rowIndex: number, colIndex: number, newValue: string) => void;
  onRequestEdit: () => void;
  onFinishEdit: () => void;
}

export const EditableCell: React.FC<EditableCellProps> = ({
  value,
  rowIndex,
  colIndex,
  isEditing,
  onCellEdit,
  onRequestEdit,
  onFinishEdit,
}) => {
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const cancelRef = useRef(false);

  useEffect(() => {
    if (isEditing) {
      setDraft(value);
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing, value]);

  const commit = () => {
    if (cancelRef.current) {
      cancelRef.current = false;
      return;
    }
    onFinishEdit();
    if (draft !== value) {
      onCellEdit(rowIndex, colIndex, draft);
    }
  };

  const cancel = () => {
    cancelRef.current = true;
    setDraft(value);
    onFinishEdit();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commit();
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      cancel();
    }
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        className="bg-muted/50 border border-primary/50 rounded px-1 py-0 text-sm outline-none w-full min-w-[60px]"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
      />
    );
  }

  return (
    <span
      className={cn(
        'cursor-text hover:bg-muted/50 rounded px-0.5 transition-colors block min-h-[1.5em] min-w-[2em]',
      )}
      onDoubleClick={(event) => {
        event.stopPropagation();
        onRequestEdit();
      }}
      title="Double-click to edit"
    >
      {value || '\u00A0'}
    </span>
  );
};

interface EditableHeaderCellProps {
  value: string;
  colIndex: number;
  onHeaderEdit: (colIndex: number, newValue: string) => void;
}

export const EditableHeaderCell: React.FC<EditableHeaderCellProps> = ({ value, colIndex, onHeaderEdit }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const cancelRef = useRef(false);

  useEffect(() => {
    if (!editing) {
      setDraft(value);
    }
  }, [value, editing]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const commit = () => {
    if (cancelRef.current) {
      cancelRef.current = false;
      return;
    }
    setEditing(false);
    if (draft !== value) {
      onHeaderEdit(colIndex, draft);
    }
  };

  const cancel = () => {
    cancelRef.current = true;
    setEditing(false);
    setDraft(value);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commit();
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      cancel();
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        className="bg-muted/50 border border-primary/50 rounded px-1 py-0 text-xs outline-none min-w-[80px]"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
      />
    );
  }

  return (
    <>
      <span>{value || '\u00A0'}</span>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          cancelRef.current = false;
          setEditing(true);
        }}
        className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground transition-opacity p-0.5"
        title="Rename column"
      >
        <Pencil className="h-3 w-3" />
      </button>
    </>
  );
};
